import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { AlertTriangle, LoaderCircle, LockKeyhole, Sparkles } from "lucide-react";
import { auth } from "../firebase";
import { bootstrapAdmin } from "../services/admin";

interface AuthValue {
  user: User;
  role: string;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export const useAdminAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAdminAuth must be used inside AdminAuth");
  return value;
};

export function PreviewAdminAuth({ children }: { children: ReactNode }) {
  const previewUser = {
    uid: "preview-owner",
    email: "owner@museshop.bg",
    displayName: "Muse Shop",
  } as User;
  return <AuthContext.Provider value={{ user: previewUser, role: "owner", signOut: async () => undefined }}>{children}</AuthContext.Provider>;
}

export function AdminAuth({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState("manager");

  const refreshClaims = async (nextUser: User | null, force = false) => {
    if (!nextUser) {
      setIsAdmin(false);
      setChecking(false);
      return;
    }
    const token = await nextUser.getIdTokenResult(force);
    setIsAdmin(token.claims.admin === true);
    setRole(typeof token.claims.role === "string" ? token.claims.role : "manager");
    setChecking(false);
  };

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setChecking(true);
    void refreshClaims(nextUser);
  }), []);

  if (checking) return <FullPageLoader label="Проверяваме достъпа…" />;
  if (!user) return <Login />;
  if (!isAdmin) return <NoAccess user={user} onActivated={() => refreshClaims(user, true)} />;

  const value: AuthValue = {
    user,
    role,
    signOut: () => firebaseSignOut(auth),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setMessage("Невалиден имейл или парола.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!email.trim()) {
      setMessage("Въведете имейла си, за да изпратим връзка за нова парола.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMessage("Изпратихме имейл за нова парола.");
    } catch {
      setMessage("Не успяхме да изпратим имейла. Проверете адреса.");
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="brand-mark"><Sparkles size={24} /></div>
        <p className="eyebrow">MUSE SHOP</p>
        <h1>Управление на магазина</h1>
        <p className="muted">Поръчки, продукти и клиенти на едно сигурно място.</p>
        <form onSubmit={submit} className="form-stack">
          <label>Имейл<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Парола<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {message && <div className="form-message">{message}</div>}
          <button className="button primary wide" disabled={busy}>{busy ? <LoaderCircle className="spin" size={18} /> : <LockKeyhole size={18} />} Вход</button>
          <button className="text-button" type="button" onClick={reset}>Забравена парола?</button>
        </form>
      </section>
    </main>
  );
}

function NoAccess({ user, onActivated }: { user: User; onActivated: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const activate = async () => {
    setBusy(true);
    try {
      await bootstrapAdmin();
      await user.getIdToken(true);
      await onActivated();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Нямате администраторски достъп.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card centered">
        <div className="brand-mark warning"><AlertTriangle size={24} /></div>
        <h1>Нужен е администраторски достъп</h1>
        <p className="muted">Влезли сте като <strong>{user.email}</strong>, но този профил още няма право да управлява магазина.</p>
        {message && <div className="form-message error">{message}</div>}
        <button className="button primary" onClick={activate} disabled={busy}>{busy ? <LoaderCircle className="spin" size={18} /> : <Sparkles size={18} />} Активирай първия администратор</button>
        <button className="text-button" onClick={() => firebaseSignOut(auth)}>Изход</button>
      </section>
    </main>
  );
}

function FullPageLoader({ label }: { label: string }) {
  return <main className="auth-page"><div className="loader-block"><LoaderCircle className="spin" /><span>{label}</span></div></main>;
}
